import * as React from 'react';
import { useCulture } from '../../culture';
import { useForkRef } from '../../hooks';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { ColorValue, ResponsiveStyleValue, SpacingValue } from '../../theming';
import { FixMeLater, inDOM } from '../../utils';
import { ButtonBase } from '../ButtonBase';
import { Styler, StylerProps } from '../Styler';
import { ScrollerContext, ScrollerContextValue } from './Scroller.context';
import { STATIC_STYLES, useDynamicStyles } from './Scroller.styles';
import { getNormalizedScrollLeft } from './utils/getNormalizedScrollLeft';
import { getScrollLeftForNormalizedValue } from './utils/getScrollLeftForNormalizedValue';
import { scrollItemToVisible } from './utils/scrollItemToVisible';

export type ScrollDirection = 'start' | 'end';
export type ScrollerNavButtonVariant = 'light' | 'dark';

function setButtonVisibility(button: HTMLDivElement, visible: boolean) {
    if (visible) {
        button.setAttribute('data-show', 'true');
    } else {
        button.removeAttribute('data-show');
    }
}

export interface ScrollerProps extends StylerProps {
    /**
     * The child components.
     */
    children?: React.ReactNode;

    /**
     * Sets the spacing that will be applied between child scroller items.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    spacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the horizontal spacing that will applied between child items. This will override `Spacing`.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    columnSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the vertical spacing that will applied between child items. This will override `Spacing`.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    rowSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Additional properties applied to the track component.
     */
    TrackProps?: StylerProps;

    /**
     * A reference passed to the `input` element.
     */
    trackRef?: React.Ref<HTMLUListElement>;

    /**
     * If `true`, fade gradients are displayed at the edges of the scroller (behind the navigation buttons)
     * to indicate that more content is available to scroll.
     * @default true
     */
    showOverflowGradient?: boolean;

    /**
     * The background color used to blend the fade gradients nto the scroller content.
     * @default background.content
     */
    overflowGradientColor?: ColorValue;

    /**
     * If `true`, disables horizontal scrolling and allows items to wrap onto the next line instead.
     * Useful when you want the layout to adapt to screen size or content density.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default false
     */
    disableScrolling?: ResponsiveStyleValue<boolean>;

    /**
     * If `true`, negative horizontal margins will be applied so the content abuts the edge of the viewport when overflowing.
     * Use when the breadcrumbs are the child of a container which applies default gutters.
     * @default false
     */
    contained?: ResponsiveStyleValue<boolean>;

    /**
     * The style variant of the navigation buttons which are displayed for non-touch interfaces.
     * @default false
     */
    buttonVariant?: ScrollerNavButtonVariant;
}

export const Scroller: React.ForwardRefExoticComponent<ScrollerProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Scroller(props: ScrollerProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            spacing = 1,
            columnSpacing: columnSpacingProp,
            rowSpacing: rowSpacingProp,
            TrackProps,
            trackRef,
            showOverflowGradient = true,
            overflowGradientColor,
            disableScrolling = false,
            contained = false,
            buttonVariant = 'light',
            ...other
        } = props;

        const isClient = inDOM();
        const { dir } = useCulture();
        const isRTL = dir === 'rtl';

        // Element refs
        const scrollerRef = React.useRef<HTMLUListElement | null>(null);
        const handleScrollerRef = useForkRef(scrollerRef, trackRef);
        const scrollButtonStartRef = React.useRef<HTMLDivElement | null>(null);
        const scrollButtonEndRef = React.useRef<HTMLDivElement | null>(null);

        // If an item is flagged as "active" then the scroller will move it into view.
        const activeItemRef = React.useRef<HTMLElement | null>(null);

        // Scroll/animation refs
        const ticking = React.useRef(false);
        const frameId = React.useRef<number | null>(null);

        const childrenArray = React.Children.toArray(children);

        // -------------------
        // Scroll helpers
        // -------------------
        const handleResize = React.useCallback(() => {
            if (!ticking.current) {
                ticking.current = true;
                frameId.current = requestAnimationFrame(() => {
                    const scroller = scrollerRef.current;
                    const item = activeItemRef.current;
                    if (scroller && item) scrollItemToVisible(scroller, item, isRTL, false);
                    ticking.current = false;
                });
            }
        }, [isRTL]);

        const handleScrollClick = React.useCallback(
            (direction: ScrollDirection) => (_event: React.SyntheticEvent) => {
                const scroller = scrollerRef.current;
                if (!scroller) return;

                const scrollDistance = scroller.clientWidth / 2;
                const current = getNormalizedScrollLeft(scroller, isRTL);
                const target = direction === 'start' ? current - scrollDistance : current + scrollDistance;

                scroller.scrollTo({
                    left: getScrollLeftForNormalizedValue(scroller, target, isRTL),
                    behavior: 'smooth',
                });
            },
            [isRTL]
        );

        // IntersectionObserver for nav buttons
        React.useEffect(() => {
            const scroller = scrollerRef.current;
            if (!isClient || !scroller) return undefined;

            const items = Array.from(scroller.children);
            if (!items.length) return undefined;

            const firstItem = items[0];
            const lastItem = items[items.length - 1];
            const observerOptions = { root: scroller, threshold: 0.99 };

            const handleIntersection = (direction: ScrollDirection) => (entries: IntersectionObserverEntry[]) => {
                const button = direction === 'start' ? scrollButtonStartRef.current : scrollButtonEndRef.current;
                const fullyVisible = entries[0]?.isIntersecting ?? false;
                if (button) setButtonVisibility(button, !fullyVisible);
            };

            const firstObserver = firstItem
                ? new IntersectionObserver(handleIntersection('start'), observerOptions)
                : null;
            const lastObserver = lastItem ? new IntersectionObserver(handleIntersection('end'), observerOptions) : null;

            firstObserver?.observe(firstItem);
            lastObserver?.observe(lastItem);

            return () => {
                firstObserver?.disconnect();
                lastObserver?.disconnect();
            };
        }, [childrenArray.length, isClient]);

        // ResizeObserver
        React.useEffect(() => {
            const scroller = scrollerRef.current;
            if (!isClient || !scroller) return undefined;

            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(scroller);

            // Ensure active item visible on mount
            const item = activeItemRef.current;
            if (scroller && item) scrollItemToVisible(scroller, item, isRTL, false);

            return () => {
                resizeObserver.disconnect();
                if (frameId.current) cancelAnimationFrame(frameId.current);
            };
        }, [isClient, handleResize, isRTL]);

        const handleChangeActiveItem = React.useCallback(
            (el: HTMLElement | null) => {
                activeItemRef.current = el;
                const scroller = scrollerRef.current;
                if (scroller && el) scrollItemToVisible(scroller, el, isRTL, true);
            },
            [isRTL]
        );

        const context: ScrollerContextValue = React.useMemo(
            () => ({
                onChangeActiveItem: handleChangeActiveItem,
            }),
            [handleChangeActiveItem]
        );

        const columnSpacing = columnSpacingProp ?? spacing;
        const rowSpacing = rowSpacingProp ?? spacing;
        const dynamicStyles = useDynamicStyles(
            columnSpacing,
            rowSpacing,
            disableScrolling,
            contained,
            overflowGradientColor
        );

        return (
            <ScrollerContext.Provider value={context}>
                <Styler ref={ref} baseStyles={[STATIC_STYLES, dynamicStyles]} data-scroller-root={true} {...other}>
                    <Styler ref={handleScrollerRef} component="ul" data-scroller-track={true} {...TrackProps}>
                        {children}
                    </Styler>
                    <div
                        ref={scrollButtonStartRef}
                        data-scroller-nav="start"
                        data-with-gradient={showOverflowGradient || undefined}
                        data-button-color={buttonVariant}
                    >
                        <ButtonBase onClick={handleScrollClick('start')}>
                            <ChevronLeftIcon biDirectional={true} />
                        </ButtonBase>
                    </div>
                    <div
                        ref={scrollButtonEndRef}
                        data-scroller-nav="end"
                        data-with-gradient={showOverflowGradient || undefined}
                        data-button-color={buttonVariant}
                    >
                        <ButtonBase onClick={handleScrollClick('end')}>
                            <ChevronRightIcon biDirectional={true} />
                        </ButtonBase>
                    </div>
                </Styler>
            </ScrollerContext.Provider>
        );
    });
